import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import i18n from "i18next";
import { supabase } from "../lib/supabase";
import { api, setAuthToken } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [session, setSession] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    const refreshProfile = useCallback(async () => {
        try {
            const { data } = await api.get("/api/auth/me");
            setProfile(data);
            if (data?.preferred_language && i18n.language !== data.preferred_language) {
                try { await i18n.changeLanguage(data.preferred_language); } catch (_) {}
            }
            return data;
        } catch (e) {
            setProfile(null);
            return null;
        }
    }, []);

    useEffect(() => {
        let mounted = true;

        const init = async () => {
            try {
                const { data: { session: s } } = await supabase.auth.getSession();
                if (!mounted) return;
                setSession(s);
                setAuthToken(s?.access_token || null);
                if (s) await refreshProfile();
            } catch (_) {
                // ignore — fall through to setLoading(false)
            } finally {
                if (mounted) setLoading(false);
            }
        };
        init();

        const { data: listener } = supabase.auth.onAuthStateChange(
            async (_event, s) => {
                setSession(s);
                setAuthToken(s?.access_token || null);
                if (s) {
                    await refreshProfile();
                } else {
                    setProfile(null);
                }
            },
        );
        return () => {
            mounted = false;
            listener?.subscription?.unsubscribe();
        };
    }, [refreshProfile]);

    const loginInitiate = async (email, password) => {
        await api.post("/api/auth/login/initiate", { email, password });
    };

    const loginVerify = async (email, code) => {
        const { data } = await api.post("/api/auth/login/verify", { email, code });
        if (data?.access_token) {
            // Cache synchronously so the next /api/auth/me call has the token
            setAuthToken(data.access_token);
            await supabase.auth.setSession({
                access_token: data.access_token,
                refresh_token: data.refresh_token || data.access_token,
            });
        }
        await refreshProfile();
        return data;
    };

    const signupInitiate = async (payload) => {
        await api.post("/api/auth/signup/initiate", payload);
    };

    const signupVerify = async (email, code) => {
        const { data } = await api.post("/api/auth/signup/verify", { email, code });
        if (data?.access_token) {
            setAuthToken(data.access_token);
            await supabase.auth.setSession({
                access_token: data.access_token,
                refresh_token: data.refresh_token || data.access_token,
            });
        }
        await refreshProfile();
        return data;
    };

    const signOut = async () => {
        // Clear local state synchronously first so UI updates instantly,
        // even if the Supabase REST call to revoke the session is slow.
        setAuthToken(null);
        setSession(null);
        setProfile(null);
        try { await supabase.auth.signOut(); } catch (_) {}
    };

    /**
     * Install a freshly-issued session (returned by backend after password or
     * email change). Avoids a round-trip through Supabase auth and keeps the
     * user logged in seamlessly.
     */
    const installSession = async (tokens) => {
        if (!tokens?.access_token) return;
        setAuthToken(tokens.access_token);
        await supabase.auth.setSession({
            access_token: tokens.access_token,
            refresh_token: tokens.refresh_token || tokens.access_token,
        });
        await refreshProfile();
    };

    return (
        <AuthContext.Provider
            value={{
                session,
                profile,
                user: session?.user || null,
                loading,
                loginInitiate,
                loginVerify,
                signupInitiate,
                signupVerify,
                signOut,
                installSession,
                refreshProfile,
                isAuthenticated: !!session,
                isOrganizer: !!profile?.is_organizer,
                isAdmin: !!profile?.is_admin,
                isStaff: !!profile?.is_staff,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
